import { useEffect, useState } from 'react';
import { useOffer } from '../../../../context/OfferProvider';
import { addDays, format, isAfter, isBefore, parse } from 'date-fns';
import { useHouseOfferSearchParams } from '../../../house-offer/context/HouseOfferSearchParamsContext';

export function useCheckoutCoreState(reservationId, globalConfig) {
  const {
    checkinTime,
    checkoutTime,
    paymentOnBooking,
    paymentOnBookingDueDays,
    securityDepositAmount: globalSecurityDepositAmount,
    isLoading
  } = globalConfig;

  const { searchParams } = useHouseOfferSearchParams();
  const {
    manualDiscounts,
    setManualDiscounts,
    manualAdditionalPayments,
    setManualAdditionalPayments,
    offerResult
  } = useOffer();

  const [customer, setCustomer] = useState(null);
  const [paymentSelections, setPaymentSelections] = useState(null);
  const [paymentOnBookingPercentage, setPaymentOnBookingPercentage] =
    useState(paymentOnBooking);
  const [securityDepositAmount, setSecurityDepositAmount] =
    useState(globalSecurityDepositAmount);
  const [additionalInformation, setAdditionalInformation] = useState({
    checkinTime,
    checkoutTime
  });
  const [formKey, setFormKey] = useState(Date.now());
  const [newCustomerReady, setNewCustomerReady] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [reservation, setReservation] = useState();
  const [editMode, setEditMode] = useState(false);
  const [checkoutStateLoading, setCheckoutStateLoading] = useState(true);
  const [consents, setConsents] = useState({
    TERMS_OF_SERVICE: false,
    PRIVACY_POLICY: false,
    CANCELLATION_POLICY: false
  });
  const [reservationLoading, setReservationLoading] = useState(!!reservationId);


  const computePaymentSelections = () => {
    const dueDays = paymentOnBookingDueDays ?? 3;
    const today = new Date();
    const todayPlusDue = addDays(today, dueDays);


    const startDateStr = searchParams?.startDate;
    const startDate = startDateStr
      ? parse(startDateStr, 'yyyy-MM-dd', new Date())
      : today;

    const latestAllowed = addDays(startDate, -1);

    const paymentDueOnBooking = !isBefore(today, startDate)
      ? today // if today >= startDate → today
      : isAfter(todayPlusDue, latestAllowed)
        ? latestAllowed
        : todayPlusDue;

    return {
      paymentModel: 'FULL_PAYMENT_ON_BOOKING',
      paymentTypeOnBooking: { id: 3 },
      paymentTypeDeposit: { id: 5 },
      paymentDueOnBooking: format(paymentDueOnBooking, 'yyyy-MM-dd'),
      paymentDueOnCheckin: startDateStr ?? format(startDate, 'yyyy-MM-dd')
    };
  };


  useEffect(() => {
    if (!isLoading) {
      setAdditionalInformation({ checkinTime, checkoutTime });
      setPaymentOnBookingPercentage(paymentOnBooking);
      setSecurityDepositAmount(globalSecurityDepositAmount);
      setCheckoutStateLoading(false);
    }
  }, [isLoading, checkinTime, checkoutTime, paymentOnBooking, globalSecurityDepositAmount]);

  useEffect(() => {
    if (!isLoading && !reservationId) {
      setPaymentSelections(computePaymentSelections());
    }
  }, [isLoading, searchParams]);

  useEffect(() => {
    if (!reservationId) return;

    setCustomer(null);
    setReservation(null);
    setFormKey(Date.now());
    setNewCustomerReady(false);
    setPaymentReady(false);
    setEditMode(false);
    setPaymentOnBookingPercentage(null);
    setSecurityDepositAmount(null);
    setAdditionalInformation(null);
    setManualDiscounts([]);
    setManualAdditionalPayments([]);
    setConsents({ TERMS_OF_SERVICE: false, PRIVACY_POLICY: false, CANCELLATION_POLICY: false });
    setPaymentSelections(computePaymentSelections());
  }, [reservationId]);

  return {
    customer,
    setCustomer,
    paymentSelections,
    setPaymentSelections,
    paymentOnBookingPercentage,
    securityDepositAmount,
    setPaymentOnBookingPercentage,
    setSecurityDepositAmount,
    additionalInformation,
    setAdditionalInformation,
    formKey,
    setFormKey,
    newCustomerReady,
    setNewCustomerReady,
    paymentReady,
    setPaymentReady,
    reservation,
    setReservation,
    editMode,
    setEditMode,
    manualDiscounts,
    setManualDiscounts,
    manualAdditionalPayments,
    setManualAdditionalPayments,
    checkoutStateLoading,
    reservationLoading,
    setReservationLoading,
    consents,
    setConsents
  };
}
